<template>
  <validation-observer tag="div" v-slot="{ validate }">
    <ui-card tag="form" :is-loading="loading || formLoading">
      <template #header>
        <h4 class="card-title">{{ title }}</h4>
      </template>

      <div class="form-group mt-0 mb-4">
        <ui-avatar
          :image="imageUrl"
          :title="$t('UI.avatar.locationTitle')"
          @save="uploadImage"
        />
      </div>

      <ui-tabs
        v-model="currentFormTab"
        :tabs="formTabList"
        type="bubble"
      >
        <template v-slot:[FORM_TABS.DEFAULT]>
          <div class="row mr-0">
            <div class="col-12 col-md-8 col-lg-6 col-xl-4">
              <validation-provider
                tag="div"
                :name="$t('form.labels.locationName')"
                rules="required"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-input
                  v-model="form.name"
                  :label="$t('form.labels.locationName')"
                  required
                  :error="errors[0]"
                />
              </validation-provider>

              <ui-input
                v-model="form.groupName"
                :label="$t('form.labels.groupName')"
              />

              <validation-provider
                tag="div"
                :name="$t('form.labels.country')"
                rules="required"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-select
                  v-model="form.country"
                  :items="countries"
                  :label="$t('form.labels.country')"
                  :placeholder="$t('form.placeholders.select')"
                  label-key="name"
                  value-key="iso"
                  filterable
                  required
                  :error="errors[0]"
                  :disabled="!editable"
                />
              </validation-provider>

              <validation-provider
                v-if="form.country"
                tag="div"
                :name="$t('form.labels.locationCurrency')"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-select
                  v-model="form.currency"
                  :items="currencyList"
                  :label="$t('form.labels.locationCurrency')"
                  :placeholder="$t('form.placeholders.select')"
                  label-key="label"
                  value-key="value"
                  filterable
                  :error="errors[0]"
                  :disabled="!editable"
                />
              </validation-provider>

              <validation-provider
                v-if="form.country"
                tag="div"
                :name="$t('form.labels.locationTimezone')"
                rules="required"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-select
                  v-model="form.timezone"
                  :items="timezoneList"
                  :label="$t('form.labels.locationTimezone')"
                  :placeholder="$t('form.placeholders.select')"
                  label-key="label"
                  value-key="value"
                  filterable
                  required
                  :error="errors[0]"
                  :disabled="!editable"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.city')"
                rules="required"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-input
                  v-model="form.city"
                  :label="$t('form.labels.city')"
                  required
                  :error="errors[0]"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.address')"
                rules="required"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-input
                  v-model="form.address"
                  :label="$t('form.labels.address')"
                  required
                  :error="errors[0]"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="'Number of locations'"
                rules="required|numeric|min_value:1"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-input
                  :value="form.locationQuantity"
                  @input="form.locationQuantity = castInt($event)"
                  :label="'Number of locations'"
                  required
                  :error="errors[0]"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.staffQty')"
                rules="required|numeric|min_value:1"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-input
                  :value="form.staffQuantity"
                  @input="form.staffQuantity = castInt($event)"
                  :label="$t('form.labels.staffQty')"
                  required
                  :error="errors[0]"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.notes')"
                rules="max:140"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-input
                  v-model="form.notes"
                  :label="$t('form.labels.notes')"
                  tag="textarea"
                  rows="1"
                  maxlength="140"
                  :error="errors[0]"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.mode')"
                rules="required"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-select
                  v-model="form.mode"
                  :items="modes"
                  :label="$t('form.labels.mode')"
                  :placeholder="$t('form.placeholders.select')"
                  label-key="label"
                  value-key="id"
                  filterable
                  required
                  :error="errors[0]"
                  :disabled="!editable"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.partner')"
                mode="passive"
                v-slot="{ errors }"
              >
                <ui-select
                  v-model="partnerStatus"
                  :items="partnerStatuses"
                  :label="$t('form.labels.partner')"
                  :placeholder="$t('form.placeholders.select')"
                  label-key="label"
                  value-key="id"
                  filterable
                  :error="errors[0]"
                />
              </validation-provider>

              <template v-if="partnerStatus">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.partnerName')"
                  rules="required"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form.partner.name"
                    :label="$t('form.labels.partnerName')"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>

                <div class="form-group mt-0 mb-5">
                  <ui-avatar
                    :image="partnerImageUrl"
                    :title="$t('UI.avatar.partnerTitle')"
                    :aspects="[
                      { aspectRatio: 1, label: 'Square' },
                      { aspectRatio: 100 / 36, label: 'Rectangle' },
                    ]"
                    @save="uploadPartnerImage"
                  />
                </div>
              </template>

              <ui-switcher
                v-for="switcher in visibleSwitchers"
                :key="switcher.key"
                v-model="form[switcher.key]"
                :label="switcher.label"
                :label-info="switcher.labelInfo"
                :disabled="switcher.disabled"
                class="mb-5"
                @input="switcher.onInput && switcher.onInput($event)"
              />
            </div>

            <location-business-type
              v-if="showExtendedForm"
              key="location-business-type"
              :form="form"
              :data="locationData"
              :is-onboarding-loading="loading"
              :editable="editable"
              @activate="activate"
              @completed="$emit('completed')"
            />
            <location-world
              v-else-if="form.country"
              :form="form"
              key="location-world"
            />
          </div>
        </template>

        <template v-slot:[FORM_TABS.PLATFORM_FEES]>
          <location-platform-fees
            :form="form"
            :workplace-id="formData && formData.id ? formData.id : null"
            :show-individual-payout-and-transfer-fees="showIndividualPayoutAndTransferFees"
            :show-common-payout-and-transfer-fees="showCommonPayoutAndTransferFees"
            :show-allow-instant-payouts="showAllowInstantPayouts"
          />
        </template>

        <template v-slot:[FORM_TABS.QRBILL]>
          <div class="mb-4">
            <div class="mb-4 font-weight-bold text-grey">
              Fees
            </div>

            <div class="row mb-4">
              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.merchantFee')"
                  rules="required|float|min_value:0|max_value:100"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.MERCHANT_FEE]"
                    :label="$t('form.labels.merchantFee')"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>

              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.gatewayFee', { currency: locationCurrency })"
                  rules="required|float|min_value:0"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.GATEWAY_FEE]"
                    :label="$t('form.labels.gatewayFee', { currency: locationCurrency })"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>

              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.settlementFee', { currency: locationCurrency })"
                  rules="required|float|min_value:0"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.SETTLEMENT_FEE]"
                    :label="$t('form.labels.settlementFee', { currency: locationCurrency })"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>
            </div>

            <div class="row mb-4">
              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.refunds', { currency: locationCurrency })"
                  rules="required|float|min_value:0"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.REFUNDS]"
                    :label="$t('form.labels.refunds', { currency: locationCurrency })"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>

              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.chargebacks', { currency: locationCurrency })"
                  rules="required|float|min_value:0"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.CHARGEBACKS]"
                    :label="$t('form.labels.chargebacks', { currency: locationCurrency })"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>
            </div>

            <div class="mb-4 font-weight-bold text-grey">
              Settlements
            </div>

            <div class="row">
              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.daysUntilSettlement')"
                  rules="required|decimal|min_value:0"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.DAYS_UNTIL_SETTLEMENT]"
                    :label="$t('form.labels.daysUntilSettlement')"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>

              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.minimumSettlementAmount', { currency: locationCurrency })"
                  rules="required|float|min_value:0"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.MINIMUM_SETTLEMENT_AMOUNT]"
                    :label="$t('form.labels.minimumSettlementAmount', { currency: locationCurrency })"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>

              <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                <validation-provider
                  tag="div"
                  :name="$t('form.labels.noLimitSettlementFee')"
                  rules="required|float|min_value:0|max_value:100"
                  mode="passive"
                  v-slot="{ errors }"
                >
                  <ui-input
                    v-model="form[QR_BILL_COMMISSION_LABELS.NO_LIMIT_SETTLEMENT_FEE]"
                    :label="$t('form.labels.noLimitSettlementFee')"
                    class="mt-2 mb-2"
                    required
                    :error="errors[0]"
                  />
                </validation-provider>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:[FORM_TABS.PAY_SETTINGS]>
          <location-pay-settings
            :location="form"
            @completed="$emit('completed')"
          />
        </template>

        <template v-slot:[FORM_TABS.INTEGRATION_SETTINGS]>
          <location-integration-settings
            :location="form"
          />
        </template>

        <template v-slot:[FORM_TABS.KYB]>
          <location-k-y-b-form
            :location-id="form.id"
            :location="form"
          />
        </template>

        <template v-slot:[FORM_TABS.KYB_FILES]>
          <location-k-y-b-files
            :location-id="form.id"
            :location="form"
          />
        </template>

        <template v-slot:[FORM_TABS.CHECK_IN_SETTINGS]>
          <location-check-in-settings
            :location="form"
          />
        </template>
      </ui-tabs>

      <div v-if="showMainSubmitActions" class="location-form__actions">
        <template v-if="formData">
          <ui-button
            action
            type="primary"
            :is-loading="loading"
            :disabled="loading"
            class="location-form__actions-button"
            @click.prevent="save(validate)"
          >
            {{ $t('form.buttons.save') }}
          </ui-button>

          <ui-button
            v-if="(formData.status === 'NEW' || formData.status === 'NOT_ACTIVE')"
            type="primary"
            :is-loading="loading"
            :disabled="loading"
            outline
            action
            class="location-form__actions-button"
            @click.prevent="activate"
          >
            {{ formData.status === 'NEW' ? $t('form.buttons.completeOnboarding') : $t('form.buttons.turnOnPay') }}
          </ui-button>

          <ui-button
            v-else-if="formData.status === 'ACTIVE'"
            type="primary"
            :is-loading="loading"
            :disabled="loading"
            @click.prevent="handleDeactivate"
            action
            outline
            class="location-form__actions-button"
          >
            {{ $t('form.buttons.turnOffPay') }}
          </ui-button>

          <ui-button
            v-if="isSubscriptionCountry(formData.country)"
            type="primary"
            outline
            action
            class="location-form__actions-button"
            @click.prevent="showSubscriptionManageModal = true"
          >
            Manage subscriptions
          </ui-button>

          <ui-button
            v-if="showCancel"
            native-type="button"
            :disabled="loading"
            class="location-form__actions-button"
            @click="cancel"
          >
            {{ $t('form.buttons.cancel') }}
          </ui-button>
        </template>

        <ui-button
          v-else
          action
          type="primary"
          :is-loading="loading"
          :disabled="loading"
          class="location-form__actions-button"
          @click.prevent="create(validate)"
        >
          {{ $t('form.buttons.create') }}
        </ui-button>
      </div>
    </ui-card>

    <location-turn-off-pay-modal
      v-model="turnOffPay"
      :data="locationData"
      @completed="deactivate"
    />

    <validation-observer tag="form" #default="{ invalid, handleSubmit }">
      <ui-modal-alert
        v-model="activateLocation.show"
        :title="'Onboarding complete date'"
        :apply-disabled="invalid"
        @apply="handleSubmit(() => activateLocation.callback(true))"
        @cancel="activateLocation.callback(false)"
      >
        <div class="row mb-0 mt-4">
          <div class="col-auto mx-auto">
            <validation-provider
              tag="div"
              :name="'Select a onboarding date'"
            >
              <ui-calendar-picker
                v-model="activateLocation.date"
                :min="minDate"
                :max="maxDate"
                :label="'Onboarding complete date'"
                :placeholder="'Select'"
                input-classes="text-center"
              />
            </validation-provider>
          </div>
        </div>
      </ui-modal-alert>
    </validation-observer>

    <subscription-manage-modal
      v-model="showSubscriptionManageModal"
      :owners="form.owners"
      :location-id="form.id"
      @submit="$emit('completed')"
      @close="showSubscriptionManageModal = false"
    />
  </validation-observer>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import api from '@/api';
import getCountry from '@/utils/getCountry';
import hex from '@/utils/hex';
import resetMixin from '@/mixins/reset-mixin';
import currencies, { LABELS } from '@/config/currencies';
import { CASE_TYPE } from '@/config/caseType';
import { COUNTRY_TIMEZONE } from '@/config/countryTimezone';
import {
  DEFAULT_FEE_PLAN,
  EMIRATES_FEE_PLAN,
  UK_FEE_PLAN,
  GERMANY_FEE_PLAN,
  DENMARK_FEE_PLAN,
  US_FEE_PLAN,
} from '@/config/feePlans';
import { QR_BILL_COMMISSION_LABELS, getQrBillCommissionConfig } from '@/config/qrBillCommission';
import {
  CANADA_CLIENT_GATEWAY_FIXED_FEES,
  CANADA_CLIENT_GATEWAY_VARIABLE_FEES,
  SAUDI_ARABIA_CLIENT_GATEWAY_FIXED_FEES,
  SAUDI_ARABIA_CLIENT_GATEWAY_VARIABLE_FEES,
  USA_CLIENT_GATEWAY_FIXED_FEES,
  USA_CLIENT_GATEWAY_VARIABLE_FEES,
} from '@/config/clientGateway';
import { FORM_TABS } from '@/config/locationFormTabs';
import { PLATFORM_FEE_TYPES } from '@/config/platformFeeTypes';
import { SWITCH_STATE } from '@/config/switchStates';
import { AB_VARIANTS_TYPES } from '@/config/abVariantsTypes';
import SubscriptionManageModal from '@/components/Subscription/SubscriptionManageModal.vue';
import { isSubscriptionCountry } from '@/config/subscription';
import toSQLDate from '@/utils/toSQLDate';
import getCurrency from '@/utils/getCurrency';
import UiTabs from '@/components/UI/Tabs/Tabs.vue';
import LocationPaySettings from '@/components/Location/LocationPaySettings.vue';
import { PAYMENT_SYSTEM } from '@/config/paymentSystems';
import { PROCESSING_METHOD } from '@/config/processingMethod';
import { PAYOUT_METHOD } from '@/config/payoutMethod';
import LocationKYBForm from '@/components/Location/LocationKYBForm.vue';
import LocationKYBFiles from '@/components/Location/LocationKYBFiles.vue';
import LocationIntegrationSettings from '@/components/Location/LocationIntegrationSettings.vue';
import { GROUP_SERVICE_AREA_STATE } from '@/config/groupServiceAreaState';
import { PAYOUT_DESTINATION } from '@/config/payoutDestination';
import { TIPS_TYPE } from '@/config/tipsTypes';
import { buildCommissionLevels } from '@/config/platformCommissionLevels';
import { getInstantPayoutsConfig } from '@/config/instantPayouts';
import LocationWorld from './LocationWorld.vue';
import LocationBusinessType from './LocationBusinessType.vue';
import LocationTurnOffPayModal from './LocationTurnOffPayModal.vue';
import LocationCheckInSettings from './LocationCheckInSettings.vue';
import LocationPlatformFees from './LocationPlatformFees.vue';

export default {

  inheritAttrs: true,

  components: {
    LocationIntegrationSettings,
    LocationKYBFiles,
    LocationKYBForm,
    LocationPaySettings,
    SubscriptionManageModal,
    LocationBusinessType,
    LocationWorld,
    LocationTurnOffPayModal,
    LocationCheckInSettings,
    LocationPlatformFees,
    UiTabs,
  },

  mixins: [
    resetMixin(() => {
      const formCountryRelation = {
        feePlan: DEFAULT_FEE_PLAN,
        staffQuantity: null,
        locationQuantity: null,
        tipsType: null,
        taxes: null,
        type: null,
        promoPeriod: 0,
        landingType: null,
        tipDistributions: null,
        distributionType: null,
        primaryStaff: null,
        primaryPercentage: 0,
        primaryTipout: 0,
        workplaceFee: null,
        paymentPage: null,
        payButtonValue: null,
        payoutPlanSetup: null,
        commonPayoutPlanSetup: null,
        processorFixed: null,
        processorVariable: null,
        enablePlatformCommissionLevels: false,
        platformFeeType: null,
        platformCommissionLevels: [],
        feeSplitPercentage: 0,
        isShowRyftAccount: null,
        paymentSystemUserId: null,
        taxReportingType: null,
        paymentSystem: null,
        tipPayoutType: null,
        caseType: null,
        processingMethod: null,
        payoutMethod: null,
        payoutDestination: null,
        clientGatewayFixedFees: 0,
        clientGatewayVariableFees: 0,

        owners: [],
        troncMasters: [],
        accountants: [],

        groups: [],
        sublocations: [],

        salesPerson: null,
        customerSuccessManager: null,
        timesheetType: null,
        distributionSchemes: null,

        abVariant: null,
        staffSortingMethod: 'RANDOM',
        typeOfReview: 'STANDARD',
        googleMapsLink: null,
        tripAdvisorLink: null,
        trustPilotLink: null,

        multiTipsMode: null,
        maxTipsAmount: null,
        isBiometryEnabled: false,
        isOnlineMenuEnabled: false,
        isEnableSubscriptionPlans: false,
        isMasterLocation: false,
        isQrBillEnabled: false,
        isSplitConfigurationEnabled: false,
        isEnableChampions: false,
        isQrCheckInEnabled: false,
        isQrForceCheckOutEnabled: false,
        enableUniQr: false,
        isGoalsEnabled: false,
        showNewDesign: false,
        enableNewRyftOnboarding: false,
        enableRyftPos: false,
        enableAdditionalPaymentMethods: false,
        enableTapToTip: false,
        enableAppReview: false,
        ryftPosReceiptPrintingEnabled: false,
        isStaffEventsEnabled: false,
        isMarketPlaceEnabled: false,
        enableDocumentStorage: false,
        isHouseTipOuts: false,
        houseTipOutsName: '',
        enableHouseTipOutsForce: false,
        isTipOutEnabled: false,
        isDetailedTransactionBreakdown: false,
        isContributorsTeamDistribution: false,
        enableInstantPayouts: false,
      };

      return {
        loading: false,
        form: {
          id: 0,
          name: null,
          country: null,
          timezone: null,
          city: null,
          address: null,
          wish: null,
          groupName: null,
          notes: '',
          partner: {
            name: '',
          },
          payrollFrequency: null,
          payrollDay: null,
          payrollCustomDate: null,
          cutOffSchedule: null,
          cutOffDay: null,
          firstCutOffDay: null,
          secondCutOffDate: null,
          mode: 'DEFAULT',
          posModel: null,
          [QR_BILL_COMMISSION_LABELS.MERCHANT_FEE]: null,
          [QR_BILL_COMMISSION_LABELS.GATEWAY_FEE]: null,
          [QR_BILL_COMMISSION_LABELS.SETTLEMENT_FEE]: null,
          [QR_BILL_COMMISSION_LABELS.REFUNDS]: null,
          [QR_BILL_COMMISSION_LABELS.CHARGEBACKS]: null,
          [QR_BILL_COMMISSION_LABELS.DAYS_UNTIL_SETTLEMENT]: null,
          [QR_BILL_COMMISSION_LABELS.MINIMUM_SETTLEMENT_AMOUNT]: null,
          [QR_BILL_COMMISSION_LABELS.NO_LIMIT_SETTLEMENT_FEE]: null,
          ...formCountryRelation,
        },
        formCountryRelation,
        imageFile: null,
        imageUrl: null,
        partnerStatus: false,
        partnerImageUrl: null,
        partnerimageFile: null,
        minDate: null,
        maxDate: null,
        activateLocation: {
          show: false,
          date: null,
          callback: () => {},
        },
        turnOffPay: false,
      };
    }),
  ],

  data() {
    return {
      FORM_TABS,
      PAYMENT_SYSTEM,
      QR_BILL_COMMISSION_LABELS,
      editable: true,
      showSubscriptionManageModal: false,
      currentFormTab: FORM_TABS.DEFAULT,
    };
  },

  props: {
    formData: Object,
    showCancel: Boolean,
    formLoading: Boolean,
  },

  computed: {
    ...mapState({
      groups: (state) => state.groups.items,
      payButtonsLoaded: (state) => state.settings.loaded.payButtons,
      salesPeopleLoaded: (state) => state.settings.loaded.salesPeople,
      distributionSchemesLoaded: (state) => state.settings.loaded.distributionSchemes,
      timezones: (state) => state.settings.timezones,
      payoutPlanSetupList: (state) => state.settings.payoutPlanSetupList,
      platformCommissionLevelsDefault: (state) => state.settings.platformCommissionLevelsDefault,
      platformFeeDefaults: (state) => state.settings.platformFeeDefaults,
    }),
    ...mapGetters({
      isAdminOrSales: 'auth/isAdminOrSales',
      defaultPayoutPlan: 'settings/defaultPayoutPlan',
      defaultCommonPayoutPlan: 'settings/defaultCommonPayoutPlan',
    }),
    showExtendedForm() {
      return this.isUk
        || this.isGermany
        || this.isDenmark
        || this.isUAE
        || this.isSwitzerland
        || this.isCanada
        || this.isSaudiArabia
        || this.isUSA;
    },
    switchers() {
      return [
        {
          key: 'isBiometryEnabled',
          label: this.$t('form.labels.enableBiometry'),
          visible: true,
          defaultOn: true,
          forcedOn: false,
        },
        {
          key: 'isOnlineMenuEnabled',
          label: this.$t('form.labels.onlineMenu'),
          visible: this.form.type === 'RESTAURANT' && !(this.isCase5 && this.isCommon),
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'isQrBillEnabled',
          label: this.$t('form.labels.enableQrBill'),
          visible: (this.isUAE && this.isPayerMax) || (this.isUk && this.isRyft),
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'isSplitConfigurationEnabled',
          label: this.$t('form.labels.enableSplitConfiguration'),
          visible: this.form.isQrBillEnabled,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'isEnableChampions',
          label: this.$t('form.labels.enableReviewChampions'),
          visible: !this.isCanada && !this.isSaudiArabia && !(this.isCase5 && this.isCommon),
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'isEnableSubscriptionPlans',
          label: this.$t('form.labels.enableSubscriptionPlans'),
          visible: this.isUk || this.isUAE,
          defaultOn: false,
          forcedOn: false,
          onInput: this.toggleSubscriptionPlansHandler,
        },
        {
          key: 'isMarketPlaceEnabled',
          label: this.$t('form.labels.enableIntegrations'),
          labelInfo: this.$t('enableIntegrations.tooltip'),
          visible: !this.isCanada && !this.isSaudiArabia && !this.isUSA,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'enableDocumentStorage',
          label: this.$t('form.labels.enableDocumentStorage'),
          labelInfo: this.$t('enableDocumentStorage.tooltip'),
          visible: this.isUk && (this.isCase2 || this.isCase5),
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'isGoalsEnabled',
          label: this.$t('form.labels.enableGoals'),
          visible: this.isPersonal || this.form.isTipOutEnabled,
          defaultOn: true,
          forcedOn: false,
        },
        {
          key: 'showNewDesign',
          label: this.$t('form.labels.newDashboard'),
          visible: true,
          defaultOn: true,
          forcedOn: this.form.payoutDestination === PAYOUT_DESTINATION.BUSINESS,
        },
        {
          key: 'enableRyftPos',
          label: this.$t('form.labels.enableRyftPos'),
          visible: this.isRyft && this.form.isMarketPlaceEnabled,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'enableNewRyftOnboarding',
          label: this.$t('form.labels.enableNewRyftOnboarding'),
          visible: this.isRyft,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'isQrCheckInEnabled',
          label: this.$t('form.labels.enableQrCheckIn'),
          visible: !this.isCase5,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'ryftPosReceiptPrintingEnabled',
          label: this.$t('form.labels.ryftPosReceiptPrintingEnabled'),
          visible: this.form.enableRyftPos,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'enableTapToTip',
          label: this.$t('form.labels.enableTapToTip'),
          visible: this.form.enableRyftPos && this.isCommon,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'isStaffEventsEnabled',
          label: this.$t('form.labels.isStaffEventsEnabled'),
          visible: this.isPersonal,
          defaultOn: true,
          forcedOn: false,
        },
        {
          key: 'enableAppReview',
          label: this.$t('form.labels.enableAppReview'),
          visible: true,
          defaultOn: false,
          forcedOn: false,
        },
        {
          key: 'enableAdditionalPaymentMethods',
          label: this.$t('form.labels.enableAdditionalPaymentMethods'),
          labelInfo: this.$t('additionalPaymentMethods.tooltip'),
          visible: this.isUSA,
          defaultOn: false,
          forcedOn: false,
        },
      ].map((switcher) => ({
        ...switcher,
        visible: Boolean(switcher.visible),
        disabled: switcher.forcedOn,
      }));
    },
    visibleSwitchers() {
      return this.switchers.filter(({ visible }) => visible);
    },
    switchersState() {
      return this.switchers.reduce((result, { key, visible, forcedOn }) => ({
        ...result,
        [key]: { visible, forcedOn },
      }), {});
    },
    locationCurrencyIso() {
      return getCurrency(this.form.country).iso;
    },
    locationCurrency() {
      // Countries may allow more than one currency, so the selected one wins over the country default.
      return LABELS[this.form.currency] || getCurrency(this.form.country).label;
    },
    // Legacy locations may have no currency stored, fall back to the country default
    // so payout plans are still requested for them.
    payoutPlanCurrency() {
      return this.form.currency || this.locationCurrencyIso;
    },
    title() {
      return this.formData ? 'Edit location' : 'Create location';
    },
    isUk() {
      return this.form.country === 'gb';
    },
    isUSA() {
      return this.form.country === 'us';
    },
    isGermany() {
      return this.form.country === 'de';
    },
    isDenmark() {
      return this.form.country === 'dk';
    },
    isUAE() {
      return this.form.country === 'ae';
    },
    isSwitzerland() {
      return this.form.country === 'ch';
    },
    isCanada() {
      return this.form.country === 'ca';
    },
    isSaudiArabia() {
      return this.form.country === 'sa';
    },
    currentClientFixedFees() {
      switch (this.form.country) {
        case 'ca':
          return CANADA_CLIENT_GATEWAY_FIXED_FEES;
        case 'sa':
          return SAUDI_ARABIA_CLIENT_GATEWAY_FIXED_FEES;
        case 'us':
          return USA_CLIENT_GATEWAY_FIXED_FEES;
        default:
          return 0;
      }
    },
    currentClientVariableFees() {
      switch (this.form.country) {
        case 'ca':
          return CANADA_CLIENT_GATEWAY_VARIABLE_FEES;
        case 'sa':
          return SAUDI_ARABIA_CLIENT_GATEWAY_VARIABLE_FEES;
        case 'us':
          return USA_CLIENT_GATEWAY_VARIABLE_FEES;
        default:
          return 0;
      }
    },
    currentFeePlan() {
      switch (this.form.country) {
        case 'ae':
          return EMIRATES_FEE_PLAN;
        case 'gb':
          return UK_FEE_PLAN;
        case 'de':
          return GERMANY_FEE_PLAN;
        case 'dk':
          return DENMARK_FEE_PLAN;
        case 'us':
          return US_FEE_PLAN;
        default:
          return DEFAULT_FEE_PLAN;
      }
    },
    currencyList() {
      return [
        {
          label: this.$t('locationCurrencyList.cad'),
          value: currencies.ca.iso,
          visible: this.isCanada,
        },
        {
          label: this.$t('locationCurrencyList.usd'),
          value: currencies.us.iso,
          visible: this.isUSA,
        },
        {
          label: this.$t('locationCurrencyList.gbp'),
          value: currencies.gb.iso,
          visible: this.isUk,
        },
        {
          label: this.$t('locationCurrencyList.eur'),
          value: currencies.eu.iso,
          visible: this.isGermany || this.isDenmark,
        },
        {
          label: this.$t('locationCurrencyList.dkk'),
          value: currencies.dk.iso,
          visible: this.isDenmark,
        },
        {
          label: this.$t('locationCurrencyList.chf'),
          value: currencies.ch.iso,
          visible: this.isSwitzerland,
        },
        {
          label: this.$t('locationCurrencyList.aed'),
          value: currencies.ae.iso,
          visible: this.isUAE,
        },
        {
          label: this.$t('locationCurrencyList.sar'),
          value: currencies.sa.iso,
          visible: this.isSaudiArabia,
        },
      ].filter(({ visible }) => visible);
    },
    timezoneList() {
      return this.timezones.map((item) => ({
        label: `${item.name} (${item.offset})`,
        value: item.name,
      }));
    },
    modes() {
      return [
        {
          id: 'DEFAULT',
          label: this.$t('locationModes.default'),
        },
        {
          id: 'DEMO',
          label: this.$t('locationModes.demo'),
        },
        {
          id: 'TEST',
          label: this.$t('locationModes.test'),
        },
      ];
    },
    partnerStatuses() {
      return [
        {
          id: true,
          label: this.$t('partnerStatuses.yes'),
        },
        {
          id: false,
          label: this.$t('partnerStatuses.no'),
        },
      ];
    },
    isNoPaymentGateway() {
      return this.form.processingMethod === PROCESSING_METHOD.NO_PAYMENT_GATEWAY;
    },
    isPersonal() {
      return this.form.tipsType === TIPS_TYPE.PERSONAL;
    },
    countries() {
      return this.$getCountries({ i18n: true });
    },
    isTipPayoutEmployer() {
      return this.form.tipPayoutType === 'EMPLOYER';
    },
    isCommon() {
      return this.form.tipsType === TIPS_TYPE.COMMON
        || this.form.tipsType === TIPS_TYPE.COMMON_PRIORITY;
    },
    isMixed() {
      return this.form.tipsType === TIPS_TYPE.MIXED;
    },
    isSelfReporting() {
      return this.form.taxReportingType === 'SELF_REPORTING';
    },
    isClientGateway() {
      return this.form.processingMethod === PROCESSING_METHOD.CLIENT_GATEWAY;
    },
    isPayerMax() {
      return this.form.paymentSystem === PAYMENT_SYSTEM.PAYER_MAX;
    },
    isRyft() {
      return this.form.paymentSystem === PAYMENT_SYSTEM.RYFT;
    },
    isCase1() {
      return this.form.caseType === CASE_TYPE.CASE_1;
    },
    isCase2() {
      return this.form.caseType === CASE_TYPE.CASE_2;
    },
    isCase3() {
      return this.form.caseType === CASE_TYPE.CASE_3;
    },
    isCase5() {
      return this.form.caseType === CASE_TYPE.CASE_5;
    },
    showIndividualPayoutAndTransferFees() {
      if (this.isClientGateway) {
        return false;
      }

      if (this.form.isTipOutEnabled) {
        return this.isCommon && (this.isCase1 || this.isCase2);
      }

      if (this.isUAE || this.isUSA) {
        if (this.isPersonal) {
          return !this.isTipPayoutEmployer;
        }

        if (this.isCommon) {
          return (this.isTipPayoutEmployer && this.isPayerMax) || this.isCase3;
        }

        if (this.isMixed) {
          return this.isCase3 || this.isCase1;
        }
      } else {
        if (this.isPersonal) {
          return (!this.isTipPayoutEmployer && this.isSelfReporting) || this.isGermany || this.isDenmark;
        }

        if (this.isCommon) {
          return this.isCase3;
        }

        if (this.isMixed) {
          return this.isCase3 || this.isCase1;
        }
      }

      return false;
    },
    showCommonPayoutAndTransferFees() {
      return this.isMixed && this.isCase3;
    },
    instantPayoutsConfig() {
      return getInstantPayoutsConfig({
        tipsType: this.form.tipsType,
        payoutDestination: this.form.payoutDestination,
        paymentSystem: this.form.paymentSystem,
        country: this.form.country,
        enableNewRyftOnboarding: this.form.enableNewRyftOnboarding,
      });
    },
    showAllowInstantPayouts() {
      return Boolean(this.instantPayoutsConfig);
    },
    showDistributionSchemes() {
      return this.isCommon || this.isMixed;
    },
    showPlatformFees() {
      return this.form.country && this.form.paymentSystem;
    },
    showIntegrations() {
      return this.form.id && (this.form.isMarketPlaceEnabled || this.form.enableRyftPos);
    },
    showKYB() {
      return this.form.id && !this.isNoPaymentGateway;
    },
    locationData() {
      return this.formData || {};
    },
    watchPlanSetupList() {
      return [
        this.form?.country || '',
        this.form?.currency || '',
        this.form?.tipsType || '',
        this.form?.isTipOutEnabled || '',
        this.form?.payoutDestination || '',
      ];
    },
    watchPayButtonsList() {
      return [
        this.form?.country || '',
        this.form?.currency || '',
      ];
    },
    watchPlatformFeesDefaults() {
      return `${this.form.country}:${this.form.paymentSystem}`;
    },
    showPayPageSettings() {
      return this.form.id && !this.isNoPaymentGateway;
    },
    showCheckInSettings() {
      return this.form.id
        && this.form.isQrCheckInEnabled;
    },
    formTabList() {
      return [
        {
          id: FORM_TABS.DEFAULT,
          title: 'General settings',
          visible: true,
          showControl: true,
        },
        {
          id: FORM_TABS.PLATFORM_FEES,
          title: 'Platform fees',
          visible: this.showPlatformFees,
          showControl: true,
        },
        {
          id: FORM_TABS.QRBILL,
          title: 'Bill settings',
          visible: this.form.isQrBillEnabled,
          showControl: true,
        },
        {
          id: FORM_TABS.PAY_SETTINGS,
          title: 'Pay page settings',
          visible: this.showPayPageSettings,
          showControl: false,
        },
        {
          id: FORM_TABS.INTEGRATION_SETTINGS,
          title: 'Integration settings',
          visible: this.showIntegrations,
          showControl: false,
        },
        {
          id: FORM_TABS.KYB,
          title: 'KYB',
          visible: this.showKYB,
          showControl: false,
        },
        {
          id: FORM_TABS.KYB_FILES,
          title: 'KYB files',
          visible: this.showKYB,
          showControl: false,
        },
        {
          id: FORM_TABS.CHECK_IN_SETTINGS,
          title: 'QR shift check-in settings',
          visible: this.showCheckInSettings,
          showControl: false,
        },
      ].filter(({ visible }) => visible);
    },
    showMainSubmitActions() {
      return this.formTabList.find(({ id }) => id === this.currentFormTab)?.showControl;
    },
    getQrBillCommissions() {
      return getQrBillCommissionConfig(this.form.country, this.form.paymentSystem);
    },
    isQrBillSettingsTab() {
      return this.currentFormTab === FORM_TABS.QRBILL;
    },
    parsedGroups() {
      return this.form.groups
        .filter(({ removed }) => !removed)
        .map(({ id, showOrder }) => ({ id, showOrder }));
    },
    watchTipsTypeReportingType() {
      return [
        this.form.tipsType,
        this.form.taxReportingType,
      ];
    },
  },

  watch: {
    formData: {
      immediate: true,
      async handler(payload) {
        const {
          country,
          primaryPercentage,
          salesPerson,
          customerSuccessManager,
          loading,
          editable,
          isBiometryEnabled,
          isOnlineMenuEnabled,
          isEnableSubscriptionPlans,
          isQrBillEnabled,
          isSplitConfigurationEnabled,
          isEnableChampions,
          isQrCheckInEnabled,
          isQrForceCheckOutEnabled,
          enableUniQr,
          isGoalsEnabled,
          showNewDesign,
          enableNewRyftOnboarding,
          enableRyftPos,
          enableAdditionalPaymentMethods,
          enableTapToTip,
          enableAppReview,
          ryftPosReceiptPrintingEnabled,
          isStaffEventsEnabled,
          isTipOutEnabled,
          isDetailedTransactionBreakdown,
          isContributorsTeamDistribution,
          isHouseTipOuts,
          houseTipOutsName,
          enableHouseTipOutsForce,
          isMarketPlaceEnabled,
          enableDocumentStorage,
          enablePlatformCommissionLevels,
          feePlan,
          enableInstantPayouts,
          ...formData
        } = payload || {};

        this.reset();
        this.loading = true;

        this.form = {
          ...this.form,
          ...formData,
          salesPerson: salesPerson || { id: null },
          customerSuccessManager: customerSuccessManager || { id: null },
          primaryPercentage,
          isBiometryEnabled: Boolean(isBiometryEnabled),
          isOnlineMenuEnabled: Boolean(isOnlineMenuEnabled),
          isEnableSubscriptionPlans: Boolean(isEnableSubscriptionPlans),
          isQrBillEnabled: Boolean(isQrBillEnabled),
          isSplitConfigurationEnabled: Boolean(isSplitConfigurationEnabled),
          isEnableChampions: Boolean(isEnableChampions),
          isQrCheckInEnabled: Boolean(isQrCheckInEnabled),
          isQrForceCheckOutEnabled: Boolean(isQrForceCheckOutEnabled),
          enableUniQr: Boolean(enableUniQr),
          isGoalsEnabled: Boolean(isGoalsEnabled),
          showNewDesign: Boolean(showNewDesign),
          enableNewRyftOnboarding: Boolean(enableNewRyftOnboarding),
          enableRyftPos: Boolean(enableRyftPos),
          enableAdditionalPaymentMethods: Boolean(enableAdditionalPaymentMethods),
          enableTapToTip: Boolean(enableTapToTip),
          enableAppReview: Boolean(enableAppReview),
          ryftPosReceiptPrintingEnabled: Boolean(ryftPosReceiptPrintingEnabled),
          isStaffEventsEnabled: Boolean(isStaffEventsEnabled),
          isTipOutEnabled: Boolean(isTipOutEnabled),
          houseTipOutsName: String(houseTipOutsName ?? ''),
          enableHouseTipOutsForce: Boolean(enableHouseTipOutsForce),
          isHouseTipOuts: Boolean(isHouseTipOuts),
          isDetailedTransactionBreakdown: Boolean(isDetailedTransactionBreakdown),
          isContributorsTeamDistribution: Boolean(isContributorsTeamDistribution),
          isMarketPlaceEnabled: Boolean(isMarketPlaceEnabled),
          enableDocumentStorage: Boolean(enableDocumentStorage),
          enablePlatformCommissionLevels: Boolean(enablePlatformCommissionLevels),
          enableInstantPayouts: Boolean(enableInstantPayouts),
        };

        if (!payload?.id) {
          this.setDefaultOnSwitchers();
        }

        if (editable !== undefined) {
          this.editable = editable;
        }

        if (primaryPercentage) {
          this.form.primaryTipout = 100 - primaryPercentage;
        }

        if (!this.form.partner) {
          this.form.partner = { name: '' };
        }

        if (this.form.partner?.id) {
          this.partnerStatus = Boolean(this.form.partner?.id);
        }

        if (payload) {
          await this.loadCountry(payload);
          await this.loadAvatar(payload);
          if (this.partnerStatus) {
            await this.loadPartnerAvatar(this.form.partner);
          }
          await this.loadUsers({ locationId: payload.id });
          await this.loadGroups({ locationId: payload.id });
          await this.loadSublocations({ locationId: payload.id });
        }

        if (payload?.id) {
          await this.loadPlatformFees(payload.id);
        }

        await this.loadPayButtons();
        await this.loadPayoutPlanSetupList();
        await this.loadFeeTypes();
        await this.loadTimezones(this.form.country);
        await this.tryLoadPlatformFeesDefaults();

        if (!this.salesPeopleLoaded) {
          await this.loadSalesPeople();
        }

        this.loading = false;
      },
    },
    switchersState(state, oldState) {
      this.switchers.forEach(({
        key,
        visible,
        defaultOn,
        forcedOn,
      }) => {
        const { visible: wasVisible, forcedOn: wasForcedOn } = oldState[key];

        if (!visible) {
          if (wasVisible) {
            this.form[key] = false;
          }

          return;
        }

        if (forcedOn && !wasForcedOn) {
          this.form[key] = true;
        } else if (!wasVisible && defaultOn && !this.form.id) {
          this.form[key] = true;
        }
      });
    },
    'form.isQrCheckInEnabled': {
      handler() {
        this.form.isQrForceCheckOutEnabled = false;
        this.form.enableUniQr = false;
      },
    },
    'form.country': {
      async handler(newValue, oldValue) {
        if (this.loading) {
          return;
        }

        this.form.processorFixed = null;
        this.form.processorVariable = null;
        this.form.maxTipsAmount = null;
        this.form.currency = null;
        this.resetQrBillSettings();

        const similarLogicCounties = ['gb', 'de', 'dk', 'ch'];
        const newIsSimilarLogicCountry = similarLogicCounties.includes(newValue);
        const oldIsSimilarLogicCountry = similarLogicCounties.includes(oldValue);

        if (newIsSimilarLogicCountry !== oldIsSimilarLogicCountry) {
          const {
            loading,
            ...formData
          } = this.formData || {};

          this.form = {
            ...this.form,
            ...this.formCountryRelation,
            ...formData,
          };
        }

        if (!this.form.id) {
          this.setDefaultOnSwitchers();
        }

        this.form.feePlan = this.currentFeePlan;

        this.form.clientGatewayFixedFees = this.currentClientFixedFees;
        this.form.clientGatewayVariableFees = this.currentClientVariableFees;
        this.form.currency = this.locationCurrencyIso;
        this.form.timezone = null;

        this.setLocationInitialValues();

        await this.loadTimezones(this.form.country);
        this.setDefaultTimezone();
        this.tryLoadPlatformFeesDefaults();
        this.tryLoadPlatformFeeDefaults();
      },
    },
    'form.processingMethod': {
      handler(value) {
        if (value === PROCESSING_METHOD.CLIENT_GATEWAY) {
          this.form.payoutPlanSetup = null;
          this.form.commonPayoutPlanSetup = null;
          this.form.paymentSystem = null;
          this.form.payoutMethod = PAYOUT_METHOD.LOCAL;
          this.form.feePlan = 0;
          this.form.clientGatewayFixedFees = this.currentClientFixedFees;
          this.form.clientGatewayVariableFees = this.currentClientVariableFees;
        }

        if (value === PROCESSING_METHOD.EASYTIP_LOCAL_GATEWAY) {
          this.form.feePlan = this.currentFeePlan;
          this.form.clientGatewayFixedFees = 0;
          this.form.clientGatewayVariableFees = 0;
        }
      },
    },
    watchPlanSetupList: {
      deep: true,
      async handler() {
        if (!this.form.country || !this.form.tipsType) {
          return;
        }

        await this.loadPayoutPlanSetupList();
        this.form.payoutPlanSetup = this.defaultPayoutPlan;
        this.form.defaultCommonPayoutPlan = this.defaultCommonPayoutPlan;
      },
    },
    watchPayButtonsList: {
      deep: true,
      async handler() {
        if (this.loading || !this.form.country) {
          return;
        }

        await this.loadPayButtons();
      },
    },
    showIndividualPayoutAndTransferFees: {
      handler(value) {
        if (value) {
          this.form.payoutPlanSetup = this.defaultPayoutPlan;
        } else {
          this.form.payoutPlanSetup = null;
        }
      },
    },
    instantPayoutsConfig: {
      handler(config) {
        if (!config) {
          this.form.enableInstantPayouts = false;

          return;
        }

        if (!this.formData?.id) {
          this.form.enableInstantPayouts = config.defaultValue;
        }
      },
    },
    showCommonPayoutAndTransferFees: {
      handler(value) {
        if (value) {
          this.form.commonPayoutPlanSetup = this.defaultCommonPayoutPlan;
        } else {
          this.form.commonPayoutPlanSetup = null;
        }
      },
    },
    watchPlatformFeesDefaults: {
      handler() {
        this.tryLoadPlatformFeesDefaults();
      },
    },
    showDistributionSchemes: {
      immediate: true,
      handler(value) {
        if (value && !this.distributionSchemesLoaded) {
          this.loadDistributionSchemes();
        }
      },
    },
    'form.isQrBillEnabled': {
      immediate: true,
      handler(payload) {
        if (!payload || !this.getQrBillCommissions) {
          return;
        }

        const fieldsSet = [
          QR_BILL_COMMISSION_LABELS.MERCHANT_FEE,
          QR_BILL_COMMISSION_LABELS.GATEWAY_FEE,
          QR_BILL_COMMISSION_LABELS.SETTLEMENT_FEE,
          QR_BILL_COMMISSION_LABELS.REFUNDS,
          QR_BILL_COMMISSION_LABELS.CHARGEBACKS,
          QR_BILL_COMMISSION_LABELS.DAYS_UNTIL_SETTLEMENT,
          QR_BILL_COMMISSION_LABELS.MINIMUM_SETTLEMENT_AMOUNT,
          QR_BILL_COMMISSION_LABELS.NO_LIMIT_SETTLEMENT_FEE,
        ].every((field) => !this.form[field]);

        if (fieldsSet) {
          const merchantFee = QR_BILL_COMMISSION_LABELS.MERCHANT_FEE;
          const gatewayFee = QR_BILL_COMMISSION_LABELS.GATEWAY_FEE;
          const settlementFee = QR_BILL_COMMISSION_LABELS.SETTLEMENT_FEE;
          const refunds = QR_BILL_COMMISSION_LABELS.REFUNDS;
          const chargebacks = QR_BILL_COMMISSION_LABELS.CHARGEBACKS;
          const daysUntilSettlement = QR_BILL_COMMISSION_LABELS.DAYS_UNTIL_SETTLEMENT;
          const minimumSettlementAmount = QR_BILL_COMMISSION_LABELS.MINIMUM_SETTLEMENT_AMOUNT;
          const noLimitSettlementFee = QR_BILL_COMMISSION_LABELS.NO_LIMIT_SETTLEMENT_FEE;

          this.form[merchantFee] = this.getQrBillCommissions[merchantFee];
          this.form[gatewayFee] = this.getQrBillCommissions[gatewayFee];
          this.form[settlementFee] = this.getQrBillCommissions[settlementFee];
          this.form[refunds] = this.getQrBillCommissions[refunds];
          this.form[chargebacks] = this.getQrBillCommissions[chargebacks];
          this.form[daysUntilSettlement] = this.getQrBillCommissions[daysUntilSettlement];
          this.form[minimumSettlementAmount] = this.getQrBillCommissions[minimumSettlementAmount];
          this.form[noLimitSettlementFee] = this.getQrBillCommissions[noLimitSettlementFee];
        }
      },
    },
    watchTipsTypeReportingType: {
      deep: true,
      handler() {
        if (this.isUk && this.isPersonal && this.isSelfReporting) {
          this.form.payrollFrequency = 'NOT_AVAILABLE';
        }
      },
    },
  },

  methods: {
    isSubscriptionCountry,
    ...mapActions({
      getGroups: 'groups/loadItems',
      loadCountryPayButtons: 'settings/loadPayButtons',
      loadDefaultCountryPayoutPlanSetupList: 'settings/loadDefaultPayoutPlanSetupList',
      loadCommonCountryPayoutPlanSetupList: 'settings/loadCommonPayoutPlanSetupList',
      loadSalesPeople: 'settings/loadSalesPeople',
      loadDistributionSchemes: 'settings/loadDistributionSchemes',
      loadFeeTypes: 'settings/loadFeeTypes',
      loadTimezones: 'settings/loadTimezones',
      loadPlatformCommissionLevels: 'settings/loadPlatformCommissionLevels',
      loadPlatformFeeDefaults: 'settings/loadPlatformFeeDefaults',
    }),
    resetQrBillSettings() {
      this.form[QR_BILL_COMMISSION_LABELS.MERCHANT_FEE] = null;
      this.form[QR_BILL_COMMISSION_LABELS.GATEWAY_FEE] = null;
      this.form[QR_BILL_COMMISSION_LABELS.SETTLEMENT_FEE] = null;
      this.form[QR_BILL_COMMISSION_LABELS.REFUNDS] = null;
      this.form[QR_BILL_COMMISSION_LABELS.CHARGEBACKS] = null;
      this.form[QR_BILL_COMMISSION_LABELS.DAYS_UNTIL_SETTLEMENT] = null;
      this.form[QR_BILL_COMMISSION_LABELS.MINIMUM_SETTLEMENT_AMOUNT] = null;
      this.form[QR_BILL_COMMISSION_LABELS.NO_LIMIT_SETTLEMENT_FEE] = null;
    },
    setDefaultOnSwitchers() {
      this.switchers
        .filter(({ defaultOn, forcedOn }) => defaultOn || forcedOn)
        .forEach(({ key, visible }) => {
          this.form[key] = visible;
        });
    },
    setLocationInitialValues() {
      this.form.locationQuantity = 1;
      this.form.staffQuantity = 10;
      this.form.abVariant = AB_VARIANTS_TYPES.DESIGN_ONE_CAPTION;
      this.form.multiTipsMode = SWITCH_STATE.ON;
      this.form.platformFeeType = PLATFORM_FEE_TYPES.ASK_CLIENT_TO_PAY;
    },
    uploadImage(data) {
      this.imageFile = data;
      this.imageUrl = URL.createObjectURL(data);
      if (this.formData?.id) {
        this.uploadAvatar({ id: this.formData.id });
      }
    },
    uploadPartnerImage(data) {
      this.partnerImageFile = data;
      this.partnerImageUrl = URL.createObjectURL(data);
      if (this.form.partner?.id) {
        this.uploadPartnerAvatar({ id: this.form.partner?.id });
      }
    },
    castInt(value) {
      return parseInt(value, 10) || 0;
    },
    loadCountry({ country }) {
      this.form.country = getCountry(country, null)?.iso;
    },
    setDefaultTimezone() {
      const defaultTimezone = COUNTRY_TIMEZONE[this.form.country];

      if (defaultTimezone) {
        this.form.timezone = defaultTimezone;
      }
    },
    async loadAvatar({ id }) {
      try {
        const { data } = await api.locations.getImage({
          locationId: id,
        });

        if (data && data.size > 0) {
          this.imageUrl = URL.createObjectURL(data);
        }
      } catch (e) {
        // noop.
      }
    },
    async loadPartnerAvatar({ id }) {
      try {
        const { data } = await api.locations.getPartnerImage({ partnerId: id });

        if (data && data.size > 0) {
          this.partnerImageUrl = URL.createObjectURL(data);
        }
      } catch (e) {
        // noop.
      }
    },
    loadUsers(payload) {
      const users = [
        'owners',
        'troncMasters',
        'accountants',
      ];

      return Promise.all(
        users.map(async (attribute) => {
          try {
            const { data } = await api[attribute].getList(payload);

            if (data) {
              this.form[attribute] = [
                ...data
                  .filter((item) => item.status !== 'NOT_ACTIVE')
                  .map((item) => ({
                    ...item,
                    oldPhoneNumber: item.phoneNumber,
                    oldEmail: item.email,
                    newest: false,
                    removed: false,
                  })),
              ];
            }
          } catch (e) {
            this.$showServerError(e);
          }
        }),
      );
    },
    async loadGroups(payload) {
      if (this.form.paymentPage !== 'GROUPS') {
        return;
      }

      try {
        await this.getGroups(payload);

        this.form.groups = this.groups.map((item) => ({
          ...item,
          removed: false,
          newest: false,
        }));
      } catch (e) {
        this.$showServerError(e);
      }
    },
    async loadSublocations(payload) {
      if (!this.form.isMasterLocation) {
        return;
      }

      try {
        const { data } = await api.locations.getSublocations(payload);

        this.form.sublocations = data;
      } catch (e) {
        this.$showServerError(e);
      }
    },
    async tryLoadPlatformFeesDefaults() {
      if (this.form?.platformCommissionLevels?.length) {
        return;
      }

      if (!this.form.country || !this.form.paymentSystem) {
        return;
      }

      try {
        await this.loadPlatformCommissionLevels({
          countryCode: this.form.country,
          paymentSystem: this.form.paymentSystem,
        });

        if (this.platformCommissionLevelsDefault.length) {
          this.form.platformCommissionLevels = buildCommissionLevels(
            this.platformCommissionLevelsDefault,
            this.form.maxTipsAmount,
          );
        }
      } catch (e) {
        // noop — defaults are optional
      }
    },
    async tryLoadPlatformFeeDefaults() {
      if (this.form.processorFixed !== null) {
        return;
      }

      if (!this.form.country) {
        return;
      }

      try {
        await this.loadPlatformFeeDefaults(this.form.country);

        if (this.platformFeeDefaults) {
          this.form.processorFixed = this.platformFeeDefaults.processorFixed;
          this.form.processorVariable = this.platformFeeDefaults.processorVariable;
          this.form.maxTipsAmount = this.platformFeeDefaults.maxTipsAmount;
        }
      } catch (e) {
        // noop — defaults are optional
      }
    },
    async loadPlatformFees(locationId) {
      try {
        const { data } = await api.locations.getPlatformFees(locationId);

        this.form.processorFixed = data.processorFixed;
        this.form.processorVariable = data.processorVariable;
        this.form.enablePlatformCommissionLevels = Boolean(data.enablePlatformCommissionLevels);
        this.form.platformCommissionLevels = this.form.enablePlatformCommissionLevels
          ? buildCommissionLevels(data.platformCommissionLevels, data.maxTipsAmount)
          : [];
        this.form.maxTipsAmount = data.maxTipsAmount;
        this.form.payoutPlanSetup = data.payoutPlanSetup || null;
        this.form.commonPayoutPlanSetup = data.commonPayoutPlanSetup || null;
        this.form.platformFeeType = data.platformFeeType;
        this.form.feeSplitPercentage = data.feeSplitPercentage;
        this.form.feePlan = data.feePlan;
      } catch (e) {
        this.$showServerError(e);
      }
    },
    async loadPayButtons() {
      this.loading = true;

      try {
        await this.loadCountryPayButtons({
          country: this.form.country,
          currency: this.payoutPlanCurrency,
        });
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading = false;
      }
    },
    async loadPayoutPlanSetupList() {
      if (this.isMixed) {
        await Promise.all([
          this.loadDefaultPayoutPlanSetupList(TIPS_TYPE.PERSONAL),
          this.loadCommonPayoutPlanSetupList(),
        ]);
      } else if (this.form.isTipOutEnabled) {
        await this.loadDefaultPayoutPlanSetupList(TIPS_TYPE.PERSONAL);
      } else {
        await this.loadDefaultPayoutPlanSetupList();
      }
    },
    async loadDefaultPayoutPlanSetupList(tipsType) {
      if (this.isSwitzerland || !this.form.country || !this.form.tipsType) {
        return;
      }

      this.loading = true;

      try {
        await this.loadDefaultCountryPayoutPlanSetupList({
          country: this.form.country,
          tipsType: tipsType ?? this.form.tipsType,
          currency: this.payoutPlanCurrency,
          ...(this.form.payoutDestination && { payoutDestination: this.form.payoutDestination }),
        });
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading = false;
      }
    },
    async loadCommonPayoutPlanSetupList() {
      if (this.isSwitzerland || !this.form.country || !this.form.tipsType) {
        return;
      }

      this.loading = true;

      try {
        await this.loadCommonCountryPayoutPlanSetupList({
          country: this.form.country,
          tipsType: TIPS_TYPE.COMMON,
          currency: this.payoutPlanCurrency,
        });
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading = false;
      }
    },
    async perform({ validate, method, resetAfter }) {
      if (!await validate()) {
        this.$warning({
          title: '',
          message: this.$t('alerts.requiredModal.message'),
        });

        return;
      }

      this.loading = true;

      try {
        const {
          promoPeriod,
          landingType,
          tipDistributions,
          owners,
          troncMasters,
          accountants,
          distributionType,
          primaryTipout,
          groups,
          salesPerson,
          partner,
          customerSuccessManager,
          distributionSchemes,
          feePlan,
          payoutPlanSetup,
          commonPayoutPlanSetup,
          processorFixed,
          processorVariable,
          enablePlatformCommissionLevels,
          platformFeeType,
          platformCommissionLevels,
          feeSplitPercentage,
          maxTipsAmount,
          ...formData
        } = this.form;

        formData.salesPerson = salesPerson?.id ? salesPerson : null;
        formData.customerSuccessManager = customerSuccessManager?.id ? customerSuccessManager : null;
        formData.houseTipOutsName = String(formData.houseTipOutsName ?? '').trim() || 'House';

        if (promoPeriod) {
          formData.promoPeriod = promoPeriod;
        }

        if (distributionType) {
          formData.distributionType = distributionType;
        }

        if (primaryTipout) {
          formData.primaryPercentage = 100 - primaryTipout;
        }

        if (formData.isQrBillEnabled) {
          formData[QR_BILL_COMMISSION_LABELS.MERCHANT_FEE] = +this.form[QR_BILL_COMMISSION_LABELS.MERCHANT_FEE];
          formData[QR_BILL_COMMISSION_LABELS.GATEWAY_FEE] = +this.form[QR_BILL_COMMISSION_LABELS.GATEWAY_FEE];
          formData[QR_BILL_COMMISSION_LABELS.SETTLEMENT_FEE] = +this.form[QR_BILL_COMMISSION_LABELS.SETTLEMENT_FEE];
          formData[QR_BILL_COMMISSION_LABELS.REFUNDS] = +this.form[QR_BILL_COMMISSION_LABELS.REFUNDS];
          formData[QR_BILL_COMMISSION_LABELS.CHARGEBACKS] = +this.form[QR_BILL_COMMISSION_LABELS.CHARGEBACKS];
          formData[QR_BILL_COMMISSION_LABELS.DAYS_UNTIL_SETTLEMENT] = +this.form[QR_BILL_COMMISSION_LABELS.DAYS_UNTIL_SETTLEMENT];
          formData[QR_BILL_COMMISSION_LABELS.MINIMUM_SETTLEMENT_AMOUNT] = +this.form[QR_BILL_COMMISSION_LABELS.MINIMUM_SETTLEMENT_AMOUNT];
          formData[QR_BILL_COMMISSION_LABELS.NO_LIMIT_SETTLEMENT_FEE] = +this.form[QR_BILL_COMMISSION_LABELS.NO_LIMIT_SETTLEMENT_FEE];
        }

        if (this.showDistributionSchemes) {
          formData.distributionSchemes = distributionSchemes;
        }

        if (this.partnerStatus) {
          formData.partner = partner;
        } else {
          formData.partner = null;
        }

        const { data: location } = await method(formData);

        await api.locations.setPlatformFees(location.id, {
          processorFixed,
          processorVariable,
          enablePlatformCommissionLevels,
          platformCommissionLevels: enablePlatformCommissionLevels ? platformCommissionLevels : [],
          maxTipsAmount,
          payoutPlanSetup,
          commonPayoutPlanSetup,
          platformFeeType,
          feeSplitPercentage,
          feePlan,
        });

        await this.uploadAvatar(location);
        if (this.partnerStatus) {
          await this.uploadPartnerAvatar(location.partner);
        }
        await this.saveUsers(location);
        await this.saveGroups(location);
        await this.saveSublocations(location);

        if (resetAfter) {
          this.reset();
          this.setDefaultOnSwitchers();
        }

        this.$emit('completed', location);
        this.$showSuccess();
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading = false;
      }
    },
    create(validate) {
      return this.perform({
        method: api.locations.createLocation,
        resetAfter: true,
        validate,
      });
    },
    save(validate) {
      return this.perform({
        method: api.locations.updateLocation,
        resetAfter: false,
        validate,
      });
    },
    async activate() {
      this.minDate = new Date(this.formData.createdDate);
      this.minDate.setDate(this.minDate.getDate() - 1);

      this.maxDate = new Date();
      this.maxDate.setDate(this.maxDate.getDate() + 1);

      return new Promise((callback) => {
        this.activateLocation = {
          show: true,
          date: null,
          callback,
        };
      }).then(async (result) => {
        if (result) {
          await api.locations.activateLocation({
            id: this.formData.id,
            onboardingDate: this.activateLocation.date,
          });

          this.$emit('update', {
            ...this.formData,
            status: 'ACTIVE',
            onboardingDate: toSQLDate(this.activateLocation.date),
          });
        }
      });
    },
    async handleDeactivate() {
      this.turnOffPay = true;
    },
    deactivate(removeReason) {
      this.$emit('update', {
        ...this.formData,
        status: 'NOT_ACTIVE',
        removeReason,
      });
    },
    async uploadAvatar({ id }) {
      if (this.imageFile) {
        await api.locations.uploadImage({
          locationId: id,
          file: this.imageFile,
        });
      }
    },
    async uploadPartnerAvatar({ id }) {
      if (this.partnerImageFile) {
        await api.locations.uploadPartnerImage({
          partnerId: id,
          file: this.partnerImageFile,
        });
      }
    },
    async saveGroups(location) {
      const normalizeGroup = (group) => {
        const isCustom = group.serviceAreaState === GROUP_SERVICE_AREA_STATE.CUSTOM;

        return {
          ...group,
          serviceAreaInputFieldText: isCustom ? group.serviceAreaInputFieldText : null,
          serviceAreaPlaceholderText: isCustom ? group.serviceAreaPlaceholderText : null,
          isServiceAreaRequired: isCustom ? group.isServiceAreaRequired : null,
        };
      };

      const groupCommonPromises = this.form.groups.map(async (item) => {
        const {
          removed,
          newest,
          ...group
        } = item;

        if (removed) {
          return api.locations.removeGroup({
            locationId: location.id,
            groupId: group.id,
          });
        }

        if (!newest) {
          const results = await Promise.allSettled([
            api.locations.updateGroup({
              locationId: location.id,
              groupId: group.id,
              ...normalizeGroup(group),
            }),
            this.updateGroupImage(location, group),
          ]);

          results
            .filter((result) => result.status === 'rejected')
            .forEach((result) => this.$showServerError(result.reason));

          return results;
        }

        return null;
      });

      const groupCreatePromises = this.form.groups.map(async (item) => {
        const {
          removed,
          newest,
          ...group
        } = item;

        if (newest) {
          const { data } = await api.locations.createGroup({
            locationId: location.id,
            ...normalizeGroup(group),
          });

          if (data.id) {
            item.id = data.id;
            return this.updateGroupImage(location, {
              ...group,
              id: data.id,
            });
          }
        }

        return null;
      });

      // Remove and update groups
      await Promise.all(groupCommonPromises);
      // Create groups after without conflict names
      await Promise.all(groupCreatePromises);
      // Update order groups
      await api.locations.updateGroupOrder(location.id, this.parsedGroups);
    },
    updateGroupImage(location, group) {
      if (group.image) {
        return api.locations.uploadGroupImage({
          locationId: location.id,
          groupId: group.id,
          file: group.image,
        });
      }

      if (group.hasImage) {
        return api.locations.deleteGroupImage({
          locationId: location.id,
          groupId: group.id,
        });
      }

      return false;
    },
    saveSublocations(location) {
      if (!this.form.isMasterLocation) {
        return false;
      }

      return Promise.allSettled([
        api.locations.setSublocation(location.id, this.form.sublocations),
        ...this.form.sublocations.map(({ id, hasImage, image }) => {
          if (image) {
            return api.locations.uploadSublocationImage({
              locationId: location.id,
              sublocationId: id,
              file: image,
            });
          }

          if (hasImage) {
            return api.locations.deleteSublocationImage({
              locationId: location.id,
              sublocationId: id,
            });
          }

          return false;
        }),
      ]);
    },
    saveUsers(location) {
      const users = [
        'owners',
        'troncMasters',
        'accountants',
      ];

      // Write hard, go pro.
      return Promise.all(
        users.map((attribute) => {
          const oldUsers = this.form[attribute]
            .filter(({ newest }) => !newest);

          const newUsers = this.form[attribute]
            .filter(({ removed }) => !removed);

          const removeUsers = oldUsers
            .filter(({ oldPhoneNumber }) => {
              return !newUsers.some(({ phoneNumber }) => phoneNumber === oldPhoneNumber);
            });

          const createUsers = newUsers
            .filter(({ id, phoneNumber }, index, list) => {
              return !list.some((item, jndex) => {
                return item.id !== id
                  && item.phoneNumber === phoneNumber
                  && index > jndex;
              });
            })
            .filter(({ phoneNumber }) => {
              return !oldUsers.some(({ oldPhoneNumber }) => oldPhoneNumber === phoneNumber);
            });

          // Add PUT endpoint for other roles if you need emails for them
          const updateUsers = attribute === 'owners' ? oldUsers
            .filter(({ email, oldEmail }) => email !== oldEmail)
            .filter((updateUser) => {
              return !createUsers.some((createUser) => updateUser.id === createUser.id);
            }) : [];

          return Promise.all([
            ...removeUsers.map((item) => {
              return api[attribute].deactivate({
                id: item.id,
                locationId: location.id,
              });
            }),
            ...createUsers.map(async (item) => {
              try {
                await api[attribute].create({
                  id: hex(16),
                  phoneNumber: item.phoneNumber,
                  email: item.email,
                  locationId: location.id,
                });
              } catch (e) {
                if (e?.response?.data?.errorCode !== 422002) {
                  throw e;
                }
              }
            }),
            ...updateUsers.map(async (item) => {
              try {
                await api[attribute].update({
                  id: item.id,
                  email: item.email,
                  locationId: location.id,
                });
              } catch (e) {
                if (e?.response?.data?.errorCode !== 422002) {
                  throw e;
                }
              }
            }),
          ]);
        }),
      );
    },
    async toggleSubscriptionPlansHandler(newValue) {
      if (
        await this.$confirm({
          title: newValue ? 'Enable plans' : 'Disable plans',
          message: newValue
            ? 'You are about to switch ON</br>subscription plans for the venue.</br></br>Features will be activated</br>depending on the subscription plan</br>and payment.'
            : 'You are about to switch OFF</br>subscription plans for the venue.</br></br>All features will be activated</br>immediately, completely and</br>unconditionally.',
          applyButton: 'Confirm',
          titleSize: 'lg',
        })
      ) {
        this.form.isEnableSubscriptionPlans = newValue;
      } else {
        this.form.isEnableSubscriptionPlans = !newValue;
      }
    },
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>
<style lang="scss">
  .location-form {
    &__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
  }
</style>
